<template>
  <div>
    <div class="page-header">
      <div class="page-header__title">后台工作台</div>
      <div class="page-header__actions">
        <el-button :loading="loading" @click="loadStats">刷新数据</el-button>
        <el-button type="warning" plain :loading="closingExpired" @click="handleCloseExpiredGroupBuy">
          处理过期拼团
        </el-button>
      </div>
    </div>

    <el-alert
      v-if="loadError"
      class="state-block"
      title="工作台数据加载失败"
      type="error"
      :closable="false"
      show-icon
    >
      <template slot>
        <span>可以先检查后端连接，或稍后重试。</span>
        <el-button type="text" @click="loadStats">重新加载</el-button>
      </template>
    </el-alert>

    <template v-else>
      <div v-if="loading" class="stats-grid">
        <el-card v-for="item in 6" :key="item" class="stat-card" shadow="never">
          <div class="skeleton-block stat-card__skeleton-label" />
          <div class="skeleton-block stat-card__skeleton-value" />
        </el-card>
      </div>

      <div v-else class="stats-grid">
        <el-card class="stat-card" shadow="hover">
          <div class="stat-card__label">分类数量</div>
          <div class="stat-card__value">{{ stats.categoryCount }}</div>
        </el-card>
        <el-card class="stat-card" shadow="hover">
          <div class="stat-card__label">商品数量</div>
          <div class="stat-card__value">{{ stats.productCount }}</div>
        </el-card>
        <el-card class="stat-card" shadow="hover">
          <div class="stat-card__label">订单数量</div>
          <div class="stat-card__value">{{ stats.orderCount }}</div>
        </el-card>
        <el-card class="stat-card" shadow="hover">
          <div class="stat-card__label">待发货订单</div>
          <div class="stat-card__value">{{ stats.pendingDeliveryCount }}</div>
        </el-card>
        <el-card class="stat-card stat-card--warm" shadow="hover">
          <div class="stat-card__label">进行中活动</div>
          <div class="stat-card__value">{{ stats.activeGroupBuyCount }}</div>
        </el-card>
        <el-card class="stat-card stat-card--warm" shadow="hover">
          <div class="stat-card__label">拼团中记录</div>
          <div class="stat-card__value">{{ stats.activeGroupBuyRecordCount }}</div>
        </el-card>
        <el-card class="stat-card stat-card--danger" shadow="hover">
          <div class="stat-card__label">待处理拼团退款</div>
          <div class="stat-card__value">{{ stats.pendingGroupBuyRefundCount }}</div>
        </el-card>
      </div>

      <div v-if="loading" class="todo-grid">
        <el-card v-for="item in 5" :key="`todo-${item}`" class="todo-card" shadow="never">
          <div class="skeleton-block todo-card__skeleton-icon" />
          <div class="skeleton-block todo-card__skeleton-count" />
          <div class="skeleton-block todo-card__skeleton-text" />
        </el-card>
      </div>

      <div v-else class="todo-grid">
        <el-card class="todo-card todo-card--warning" shadow="hover" @click.native="goOrderList(0)">
          <div class="todo-card__badge">待付款</div>
          <div class="todo-card__count">{{ todos.pendingPay }}</div>
          <div class="todo-card__desc">需要关注未支付订单，及时排查异常支付。</div>
        </el-card>
        <el-card class="todo-card todo-card--primary" shadow="hover" @click.native="goOrderList(1)">
          <div class="todo-card__badge">待发货</div>
          <div class="todo-card__count">{{ todos.pendingDelivery }}</div>
          <div class="todo-card__desc">优先处理待发货订单，保证履约时效。</div>
        </el-card>
        <el-card class="todo-card todo-card--success" shadow="hover" @click.native="goOrderList(2)">
          <div class="todo-card__badge">待收货</div>
          <div class="todo-card__count">{{ todos.pendingReceive }}</div>
          <div class="todo-card__desc">可以关注物流状态和签收异常。</div>
        </el-card>
        <el-card class="todo-card todo-card--group" shadow="hover" @click.native="$router.push('/group-buy')">
          <div class="todo-card__badge">团购中</div>
          <div class="todo-card__count">{{ todos.activeGroupBuying }}</div>
          <div class="todo-card__desc">当前正在进行中的拼团记录，建议及时关注成团进度。</div>
        </el-card>
        <el-card class="todo-card todo-card--danger" shadow="hover" @click.native="$router.push('/group-buy/refunds')">
          <div class="todo-card__badge">待退款</div>
          <div class="todo-card__count">{{ todos.pendingGroupBuyRefund }}</div>
          <div class="todo-card__desc">拼团失败后尚未完成退款的订单，建议优先核查。</div>
        </el-card>
      </div>

      <div v-if="loading" class="panel-grid">
        <el-card v-for="item in 4" :key="`panel-${item}`" class="panel" shadow="never">
          <div class="skeleton-block panel__skeleton-title" />
          <div class="skeleton-block panel__skeleton-line" />
          <div class="skeleton-block panel__skeleton-line panel__skeleton-line--short" />
          <div class="skeleton-block panel__skeleton-button" />
        </el-card>
      </div>

      <div v-else class="panel-grid">
        <el-card class="panel" shadow="hover">
          <h3>分类管理</h3>
          <p>维护商城分类、排序和启用状态。</p>
          <el-button type="primary" plain @click="$router.push('/categories')">进入分类管理</el-button>
        </el-card>
        <el-card class="panel" shadow="hover">
          <h3>商品管理</h3>
          <p>新增商品、维护 SKU，并处理上下架状态。</p>
          <el-button type="primary" plain @click="$router.push('/products')">进入商品管理</el-button>
        </el-card>
        <el-card class="panel" shadow="hover">
          <h3>订单管理</h3>
          <p>查看订单详情，处理待发货订单。</p>
          <el-button type="primary" plain @click="$router.push('/orders')">进入订单管理</el-button>
        </el-card>
        <el-card class="panel" shadow="hover">
          <h3>团购活动</h3>
          <p>查看进行中的团购活动和拼团记录，处理过期拼团和退款。</p>
          <div class="panel__actions">
            <el-button type="primary" plain @click="$router.push('/group-buy')">进入团购活动</el-button>
            <el-button type="warning" plain :loading="closingExpired" @click="handleCloseExpiredGroupBuy">
              处理过期拼团
            </el-button>
          </div>
        </el-card>
      </div>

      <el-card class="recent-card" shadow="never">
        <div slot="header" class="recent-card__header">
          <span>最近订单</span>
          <el-button type="text" @click="$router.push('/orders')">查看全部</el-button>
        </div>

        <div v-if="loading" class="recent-list">
          <div v-for="item in 4" :key="`recent-skeleton-${item}`" class="recent-skeleton">
            <div class="skeleton-block recent-skeleton__title" />
            <div class="skeleton-block recent-skeleton__line" />
          </div>
        </div>

        <el-empty v-else-if="!recentOrders.length" description="暂时还没有订单数据" />

        <el-table v-else :data="recentOrders" border>
          <el-table-column prop="orderNo" label="订单号" min-width="220" />
          <el-table-column prop="receiverName" label="收货人" width="120" />
          <el-table-column prop="payAmountText" label="订单金额" width="120" />
          <el-table-column prop="orderTypeText" label="订单类型" width="100" />
          <el-table-column prop="orderStatusText" label="状态" width="120" />
          <el-table-column prop="createTimeText" label="创建时间" min-width="180" />
          <el-table-column label="操作" width="120">
            <template slot-scope="{ row }">
              <el-button type="text" @click="goOrderDetail(row.orderNo)">查看详情</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </template>
  </div>
</template>

<script>
import {
  closeExpiredGroupBuy,
  getCategoryList,
  getGroupBuyStats,
  getOrderPage,
  getProductPage
} from '../api/admin'
import { formatDateTime, formatPrice } from '../utils/format'

export default {
  data() {
    return {
      loading: false,
      loadError: false,
      closingExpired: false,
        stats: {
          categoryCount: 0,
          productCount: 0,
          orderCount: 0,
          pendingDeliveryCount: 0,
          activeGroupBuyCount: 0,
          activeGroupBuyRecordCount: 0,
          pendingGroupBuyRefundCount: 0
        },
        todos: {
          pendingPay: 0,
          pendingDelivery: 0,
          pendingReceive: 0,
          activeGroupBuying: 0,
          pendingGroupBuyRefund: 0
        },
      recentOrders: []
    }
  },
  created() {
    this.loadStats()
  },
  methods: {
    async loadStats() {
      this.loading = true
      this.loadError = false
      try {
        const results = await Promise.allSettled([
          getCategoryList(),
          getProductPage({ pageNum: 1, pageSize: 1 }),
          getOrderPage({ pageNum: 1, pageSize: 1 }),
          getOrderPage({ pageNum: 1, pageSize: 1, orderStatus: 0 }),
          getOrderPage({ pageNum: 1, pageSize: 1, orderStatus: 1 }),
          getOrderPage({ pageNum: 1, pageSize: 1, orderStatus: 2 }),
          getOrderPage({ pageNum: 1, pageSize: 5 }),
          getGroupBuyStats()
        ])
        const values = results.map(item => (item.status === 'fulfilled' ? item.value : null))
        const [
          categories,
          productPage,
          orderPage,
          pendingPayPage,
          pendingDeliveryPage,
          pendingReceivePage,
          recentOrderPage,
          groupBuyStats
        ] = values
        const coreFailed = !categories || !productPage || !orderPage
        if (coreFailed) {
          throw new Error('工作台核心数据加载失败')
        }
        this.stats = {
          categoryCount: (categories || []).length,
          productCount: productPage.total || 0,
          orderCount: orderPage.total || 0,
          pendingDeliveryCount: pendingDeliveryPage.total || 0,
          activeGroupBuyCount: groupBuyStats.activeActivityCount || 0,
          activeGroupBuyRecordCount: groupBuyStats.activeRecordCount || 0,
          pendingGroupBuyRefundCount: groupBuyStats.pendingRefundCount || 0
        }
        this.todos = {
          pendingPay: pendingPayPage.total || 0,
          pendingDelivery: pendingDeliveryPage.total || 0,
          pendingReceive: pendingReceivePage.total || 0,
          activeGroupBuying: groupBuyStats.activeRecordCount || 0,
          pendingGroupBuyRefund: groupBuyStats.pendingRefundCount || 0
        }
        this.recentOrders = (recentOrderPage.list || []).map(item => ({
          ...item,
          payAmountText: `￥${formatPrice(item.payAmount)}`,
          createTimeText: formatDateTime(item.createTime),
          orderTypeText: Number(item.orderType) === 1 ? '团购单' : '普通单',
          orderStatusText: this.orderStatusText(item.orderStatus)
        }))
      } catch (error) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },
    orderStatusText(status) {
      const map = {
        0: '待付款',
        1: '待发货',
        2: '待收货',
        3: '已完成',
        4: '已取消'
      }
      return map[status] || '未知状态'
    },
    async handleCloseExpiredGroupBuy() {
      this.closingExpired = true
      try {
        const summary = await closeExpiredGroupBuy()
        const idleClosedCount = Number(summary?.idleClosedCount || 0)
        const failedRecordCount = Number(summary?.failedRecordCount || 0)
        const refundOrderCount = Number(summary?.refundOrderCount || 0)
        const refundProcessedCount = Number(summary?.refundProcessedCount || 0)
        this.$message.success(`已处理过期拼团：空团关闭 ${idleClosedCount} 条，失败关团 ${failedRecordCount} 条，待退款订单 ${refundOrderCount} 笔，已尝试发起退款 ${refundProcessedCount} 笔`)
        await this.loadStats()
      } catch (error) {
        this.$message.error(error?.message || '过期拼团处理失败')
      } finally {
        this.closingExpired = false
      }
    },
    goOrderDetail(orderNo) {
      this.$router.push(`/orders/${orderNo}`)
    },
    goOrderList(orderStatus) {
      this.$router.push({
        path: '/orders',
        query: {
          q: JSON.stringify({
            orderNo: '',
            receiverMobile: '',
            orderStatus,
            pageNum: 1,
            pageSize: 20
          })
        }
      })
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-header__title {
  font-size: 20px;
  font-weight: 600;
  color: #2f2416;
}

.page-header__actions {
  display: flex;
  gap: 12px;
}

.state-block {
  margin-top: 20px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 20px;
  margin-top: 24px;
}

.stat-card__label {
  color: #8b7a68;
  font-size: 14px;
}

.stat-card__value {
  margin-top: 10px;
  font-size: 32px;
  font-weight: 700;
  color: #2f2416;
}

.stat-card--warm .stat-card__value {
  color: #b45309;
}

.stat-card--danger .stat-card__value {
  color: #c2410c;
}

.stat-card__skeleton-label {
  width: 88px;
  height: 16px;
}

.stat-card__skeleton-value {
  width: 72px;
  height: 38px;
  margin-top: 16px;
}

.todo-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 20px;
  margin-top: 24px;
}

.todo-card {
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.todo-card:hover {
  transform: translateY(-2px);
}

.todo-card__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 13px;
  font-weight: 600;
}

.todo-card__count {
  margin-top: 18px;
  font-size: 40px;
  line-height: 1;
  font-weight: 700;
  color: #1f2937;
}

.todo-card__desc {
  margin-top: 12px;
  color: #6b7280;
  line-height: 1.7;
}

.todo-card--warning .todo-card__badge {
  background: #fff7ed;
  color: #c2410c;
}

.todo-card--primary .todo-card__badge {
  background: #eff6ff;
  color: #1d4ed8;
}

.todo-card--success .todo-card__badge {
  background: #ecfdf5;
  color: #047857;
}

.todo-card--group .todo-card__badge {
  background: #fff7ed;
  color: #b45309;
}

.todo-card--danger .todo-card__badge {
  background: #fef2f2;
  color: #b91c1c;
}

.todo-card__skeleton-icon {
  width: 72px;
  height: 28px;
  border-radius: 999px;
}

.todo-card__skeleton-count {
  width: 80px;
  height: 42px;
  margin-top: 18px;
}

.todo-card__skeleton-text {
  width: 100%;
  height: 16px;
  margin-top: 16px;
}

.panel-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  margin-top: 24px;
}

.panel h3 {
  margin: 0 0 10px;
}

.panel p {
  min-height: 44px;
  color: #6b7280;
}

.panel__actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.panel__skeleton-title {
  width: 96px;
  height: 22px;
}

.panel__skeleton-line {
  width: 100%;
  height: 14px;
  margin-top: 14px;
}

.panel__skeleton-line--short {
  width: 78%;
}

.panel__skeleton-button {
  width: 132px;
  height: 32px;
  margin-top: 24px;
  border-radius: 16px;
}

.recent-card {
  margin-top: 24px;
}

.recent-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.recent-list {
  display: grid;
  gap: 16px;
}

.recent-skeleton {
  padding: 4px 0;
}

.recent-skeleton__title {
  width: 220px;
  height: 16px;
}

.recent-skeleton__line {
  width: 100%;
  height: 14px;
  margin-top: 12px;
}
</style>
